// Client-side mirror of backend conditional visibility + labels.

export function isVisible(question, responses) {
  const cond = question.conditional;
  if (!cond) return true;
  const val = responses[cond.question];
  if (cond.op === "equals") return val === cond.value;
  if (cond.op === "not_equals") return val !== cond.value;
  if (cond.op === "includes") return Array.isArray(val) && val.includes(cond.value);
  return true;
}

export function visibleQuestions(module, responses) {
  return module.questions.filter((q) => isVisible(q, responses));
}

export const MODULE_STATUS = {
  not_started: { label: "Not Started", tone: "slate" },
  in_progress: { label: "In Progress", tone: "blue" },
  customer_completed: { label: "Customer Completed", tone: "indigo" },
  needs_clarification: { label: "Needs Clarification", tone: "amber" },
  consultant_reviewed: { label: "Consultant Reviewed", tone: "violet" },
  validated: { label: "Validated", tone: "green" },
};

export const PROJECT_STATUS = {
  discovery_in_progress: { label: "Discovery In Progress", tone: "blue" },
  awaiting_customer: { label: "Awaiting Customer", tone: "amber" },
  awaiting_review: { label: "Awaiting Review", tone: "violet" },
  completed: { label: "Completed", tone: "green" },
};

export function isAnswered(v) {  if (v === null || v === undefined || v === "") return false;
  if (Array.isArray(v)) return v.length > 0;
  if (typeof v === "object") return Object.keys(v).length > 0;
  return true;
}

export function formatValue(q, v) {
  if (!isAnswered(v)) return "—";
  if (q.type === "yes_no") return v ? "Yes" : "No";
  if (q.type === "multi_select" || q.type === "single_select" || q.type === "dropdown") {
    const opts = q.options || [];
    const toLabel = (x) => opts.find((o) => o.value === x)?.label || x;
    return Array.isArray(v) ? v.map(toLabel).join(", ") : toLabel(v);
  }
  if (q.type === "process_steps") return (v || []).map((s, i) => `${i + 1}. ${s.text}`).join("  |  ");
  if (q.type === "table") {
    const cols = (q.options || []).map((o) => o.value);
    return (v || []).map((r) => (cols.length ? cols.map((c) => r[c]).filter(Boolean) : Object.values(r).filter(Boolean)).join(" — ")).join("  |  ");
  }
  if (q.type === "tags") return Array.isArray(v) ? v.join(", ") : String(v);
  if (q.type === "hierarchy") return Array.isArray(v) ? v.filter(Boolean).join(" → ") : String(v);
  if (q.type === "file_upload") return v?.name || (v ? "Uploaded" : "");
  return String(v);
}

// ---------------------------------------------------------------- scope
export const SCOPE_META = {
  standard: { label: "Standard", tone: "slate", rank: 1 },
  standard_config: { label: "Standard + Config", tone: "blue", rank: 2 },
  customization: { label: "Customization", tone: "violet", rank: 3 },
  out_of_scope: { label: "Out of Scope", tone: "amber", rank: 0 },
};

export const SCOPE_ORDER = ["standard", "standard_config", "customization"];

export const EXPANSION_STATUS = {
  pending: { label: "Pending review", tone: "amber" },
  future_backlog: { label: "Future Backlog", tone: "slate" },
  change_request: { label: "Scope / Change Request", tone: "violet" },
  approved_in_scope: { label: "Approved In-Scope", tone: "green" },
};

export function scopeRank(s) {
  return SCOPE_META[s]?.rank ?? 0;
}

export function scopeLabel(s) {
  return SCOPE_META[s]?.label || s;
}

// Detect if an answer selects an option that implies deeper scope than allowed.
export function detectExpansion(question, value, moduleScope) {
  if (!moduleScope || moduleScope === "out_of_scope") return null;
  const selected = Array.isArray(value) ? value : [value];
  let best = null;
  (question.options || []).forEach((o) => {
    if (o.scope && selected.includes(o.value) && scopeRank(o.scope) > scopeRank(moduleScope)) {
      if (!best || scopeRank(o.scope) > scopeRank(best.scope)) best = { label: o.label, scope: o.scope };
    }
  });
  return best;
}
